//@flow
import * as Immutable from 'immutable';

import { MasterRider } from 'common/models/flow/MasterRider';
import type { MasterRiderModelType } from 'common/models/flow/MasterRider';

import { Competency } from './Competency';
import { PjpRiderData } from './PjpRiderData';
import { RiderPersona } from './MasterRiderPersona';
import { RiderPersonaId } from './RiderPersonaId';
import { ShiftsInfo } from './ShiftsInfo';
import { TeamId } from './TeamId';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';
import { DayShift } from './DayShift';
import { AuditMetadata } from './AuditMetadata';

import {
    validatable,
    validate,
    validateAll,
    validateId,
    softValidateAll
} from 'common/utils/ValidationUtil';
import I18nHelper from 'common/helpers/I18nHelper';
import RegExUtil from 'common/utils/RegExUtil';

import { PhoneNumber } from './PhoneNumber';

import { BioData, EmailAddress } from './BioData';
import DataUtils from 'common/utils/DataUtils';

import EntityTypeEnum from 'common/enums/EntityTypeEnum';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import { Limits } from 'common/models/internal/Limits';
import { SkillId } from 'common/models/internal/SkillId';

import { Otp } from './Otp';

const translationsNamespace = ['entityManagement', 'validations'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

const ENTITY_NAME_ID = 'masterRider';

export const RiderTypeType = new EntityTypeEnum({
    Rider: 'RIDER',
    Cashier: 'CASHIER',
    Sorter: 'SORTER',
    Salesman: 'SALESMAN'
});

@validatable
class InternalMasterRider extends MasterRider {
    static fromJS(json: MasterRiderModelType): InternalMasterRider {
        const state: Object = Object.assign({}, json);
        state.customProperties = Immutable.Map(state.customProperties);
        state.phoneNumber = PhoneNumber.fromJS(state.phoneNumber);
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.competency = state.competency
            ? Competency.fromJS(state.competency)
            : state.competency;
        state.pjpData = state.pjpData
            ? PjpRiderData.fromJS(state.pjpData)
            : state.pjpData;
        state.shiftsInfo = state.shiftsInfo
            ? ShiftsInfo.fromJS(state.shiftsInfo)
            : state.shiftsInfo;
        state.bioData = state.bioData
            ? BioData.fromJS(state.bioData)
            : state.bioData;
        state.riderPersona = state.riderPersona
            ? RiderPersona.fromJS(state.riderPersona)
            : state.riderPersona;
        state.riderPersonaId = state.riderPersonaId
            ? RiderPersonaId.fromJS(state.riderPersonaId)
            : state.riderPersonaId;
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.skills = Immutable.List(state.skills).map(item =>
            SkillId.fromJS(item)
        );
        state.limits = state.limits
            ? Limits.fromJS(state.limits)
            : state.limits;
        state.otpInformation = state.otpInformation
            ? Otp.fromJS(state.otpInformation)
            : state.otpInformation;
        return new this(Immutable.Map(state));
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        const { pjpData, competency, bioData, shiftsInfo, limits } = this;
        if (pjpData && pjpData.hasValidationErrors()) {
            return true;
        }
        if (competency && competency.hasValidationErrors()) {
            return true;
        }
        if (bioData && bioData.hasValidationErrors()) {
            return true;
        }
        if (shiftsInfo && shiftsInfo.hasValidationErrors()) {
            return true;
        }
        if (limits?.hasValidationErrors()) {
            return true;
        }
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        let { pjpData, competency, bioData, shiftsInfo, limits } = this;
        if (pjpData) {
            pjpData = pjpData.setAllValidationErrors();
        }
        if (competency) {
            competency = competency.setAllValidationErrors();
        }
        if (bioData) {
            bioData = bioData.setAllValidationErrors();
        }
        if (shiftsInfo) {
            shiftsInfo = shiftsInfo.setAllValidationErrors();
        }
        if (limits) {
            limits = limits.setAllValidationErrors();
        }
        return this.setPjpData(pjpData)
            .setCompetency(competency)
            .setBioData(bioData)
            .setShiftsInfo(shiftsInfo)
            .setLimits(limits);
    }

    setHiddenModelProperties(hiddenModelProperties: Immutable.List<string>) {
        return this.clone(
            this._state.set('hiddenModelProperties', hiddenModelProperties)
        );
    }

    getHiddenModelProperties(): Immutable.List<string> {
        return this._state.get('hiddenModelProperties');
    }

    getDefaultSlots(): Immutable.List<Object> {
        const days = Object.values(DayOfWeekType);
        const sortedWorkingDays = [];
        days.forEach(day => {
            const dayShift = DayShift.fromJS({
                dayOfWeek: day,
                slot: { start: '', end: '' },
                breaks: []
            });
            sortedWorkingDays.push(dayShift);
        });
        return Immutable.List(sortedWorkingDays);
    }

    isHidden(key: string) {
        const hiddenModelProperties = this.getHiddenModelProperties();
        const [groupKey] = key.split('.');
        return (
            (hiddenModelProperties && hiddenModelProperties.includes(key)) ||
            (hiddenModelProperties && hiddenModelProperties.includes(groupKey))
        );
    }

    sanitize(): this {
        return this;
    }

    @validate
    setId(id: string): this {
        return super.setId(id);
    }

    validateId() {
        return validateId(this.id);
    }

    @validate
    setCode(code: string): this {
        return super
            .setCode(code)
            .setId(!DataUtils.isEmpty(code) ? RegExUtil.conformToId(code) : '');
    }

    validateCode() {
        const code = this.code || this.id;
        if (DataUtils.isEmpty(code)) {
            return nsTranslate('entityValidationErrors.codeEmpty');
        }
        if (code.trim().length < 2) {
            return nsTranslate('entityValidationErrors.codeLessThan2');
        }
        if (code.trim().length > 32) {
            return nsTranslate('entityValidationErrors.codeGreaterThan32');
        }
    }

    @validate
    setName(name: string): this {
        return super.setName(name);
    }

    validateName() {
        if (
            DataUtils.isEmpty(this.name) ||
            DataUtils.isEmpty(this.name.trim())
        ) {
            return nsTranslate('entityValidationErrors.nameEmpty', {
                entityNameId: ENTITY_NAME_ID
            });
        }
    }

    @validate
    setType(type: string): this {
        return super.setType(type);
    }

    validateType() {
        let validationMessage = null;
        const riderTypeValues = RiderTypeType.values();
        if (
            !this.type ||
            !this.type.trim() ||
            !riderTypeValues.includes(this.type)
        ) {
            validationMessage = nsTranslate(
                'entityValidationErrors.typeEnumValidation',
                {
                    typeList: riderTypeValues.join(', '),
                    entityNameId: ENTITY_NAME_ID
                }
            );
        }
        return validationMessage;
    }

    @validate
    setPhoneNumber(phoneNumber: PhoneNumber): this {
        return super.setPhoneNumber(phoneNumber);
    }

    validatePhoneNumber() {
        if (
            DataUtils.isEmpty(this.phoneNumber.phoneNumber) ||
            DataUtils.isEmpty(this.phoneNumber.phoneNumber.trim())
        ) {
            return nsTranslate(
                'validations:riderValidationErrors.phoneNumberEmpty'
            );
        }
        return this.phoneNumber.validatePhoneNumber();
    }

    setShouldSkipRiderPersonaIdValidation(flag: boolean): this {
        return this.clone(
            this._state.set('shouldSkipRiderPersonaIdValidation', flag)
        );
    }

    getShouldSkipRiderPersonaIdValidation(): boolean {
        return this._state.get('shouldSkipRiderPersonaIdValidation');
    }

    setRiderPersona(riderPersona: ?RiderPersona): this {
        let rider = this.clone(this._state.set('riderPersona', riderPersona));
        if (!riderPersona?.enablePjp) {
            rider = rider.setPjpData(
                this.pjpData.setShouldSkipSalesCategoryIdsValidation(true)
            );
        }
        return rider;
    }

    @validate
    setRiderPersonaId(riderPersonaId: RiderPersonaId): this {
        return super.setRiderPersonaId(riderPersonaId);
    }

    validateRiderPersonaId() {
        if (
            DataUtils.isEmpty(this.riderPersonaId) ||
            DataUtils.isEmpty(this.riderPersonaId.riderPersonaId)
        ) {
            return nsTranslate(
                'validations:riderValidationErrors.personaIdEmpty'
            );
        }
    }

    getCellValidationMessage({ header, validationData }) {
        return EntityImportDataHelper.getCellValidationMessage({
            entity: this,
            header,
            validationData
        });
    }

    get phoneNumber(): PhoneNumber {
        return this._state.get('phoneNumber');
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    @validate
    setTeams(teams: Immutable.List<TeamId>): this {
        return this.clone(this._state.set('teams', teams));
    }

    validateTeams(): ?string {
        if (DataUtils.isEmpty(this.teams) || !this.teams.size) {
            return nsTranslate('validations:riderValidationErrors.teamEmpty');
        }
    }

    getShouldSkipTeamsValidation(): boolean {
        return this._state.get('shouldSkipTeamsValidation');
    }

    setShouldSkipTeamsValidation(flag: boolean): this {
        return this.clone(this._state.set('shouldSkipTeamsValidation', flag));
    }

    get competency(): ?Competency {
        return this._state.get('competency');
    }

    get pjpData(): ?PjpRiderData {
        return this._state.get('pjpData');
    }

    get shiftsInfo(): ?ShiftsInfo {
        return this._state.get('shiftsInfo');
    }

    get bioData(): ?BioData {
        return this._state.get('bioData');
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(): ?this {
        let sanitizedRider = this.sanitize();
        sanitizedRider = super.defaultSanitize(sanitizedRider);
        if (sanitizedRider) {
            const sanitizedCompetency = sanitizedRider.competency?.defaultSanitize();
            const sanitizedPjpData = sanitizedRider.pjpData?.defaultSanitize();
            const sanitizedBioData = sanitizedRider.bioData?.defaultSanitize();
            const sanitizedShiftInfo = sanitizedRider.shiftsInfo?.defaultSanitize();
            const sanitizedLimits = sanitizedRider.limits?.defaultSanitize();

            return sanitizedRider
                .setCompetency(sanitizedCompetency)
                .setPjpData(sanitizedPjpData)
                .setBioData(sanitizedBioData)
                .setShiftsInfo(sanitizedShiftInfo)
                .setLimits(sanitizedLimits);
        }
        return sanitizedRider;
    }

    static defaultInit({ props }): InternalMasterRider {
        const competency = Competency.defaultInit({ props });
        const pjpData = PjpRiderData.defaultInit({ props });
        const shiftsInfo = ShiftsInfo.defaultInit({ props });
        const bioData = BioData.defaultInit({ props });
        const limits = Limits.defaultInit({ props });
        const arePjpFieldsDisabled = props.isFieldHidden?.('pjp');
        const isEmailValidationRequired =
            props.entitySettings.riderSettings.enforceEmailUniqueness;
        const userEmail = bioData.emailAddress;
        return InternalMasterRider.fromJS({
            type: arePjpFieldsDisabled
                ? RiderTypeType.Rider
                : RiderTypeType.Salesman
        })
            .setCompetency(competency)
            .setPjpData(
                pjpData.setShouldSkipSalesCategoryIdsValidation(
                    arePjpFieldsDisabled
                )
            )
            .setShiftsInfo(shiftsInfo)
            .setBioData(
                bioData
                    .setEmailAddress(userEmail || EmailAddress.fromJS({}))
                    .setShouldValidateEmailAddress(isEmailValidationRequired)
            )
            .setShouldSkipRiderPersonaIdValidation(
                props.isFieldHidden?.('riderPersona')
            )
            .setLimits(limits);
    }

    setValidationBasedOnEntityData() {
        let _state = this.clone(this._state);
        const riderPersonaPjpDisabled = !this.riderPersona?.enablePjp;

        if (riderPersonaPjpDisabled) {
            _state = _state.setPjpData(
                this.pjpData.setShouldSkipSalesCategoryIdsValidation(true)
            );
        }

        return _state;
    }

    get riderPersona(): ?RiderPersona {
        return this._state.get('riderPersona');
    }

    get riderPersonaId(): ?RiderPersonaId {
        return this._state.get('riderPersonaId');
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    get skills(): Immutable.List<SkillId> {
        return this._state.get('skills');
    }

    get limits(): ?Limits {
        return this._state.get('limits');
    }

    get otpInformation(): ?Otp {
        return this._state.get('otpInformation');
    }
}

export { InternalMasterRider as MasterRider };
